import { useMemo } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { DatePicker, Select } from 'antd';
import { PageHeader } from '../components/yeni-design/primitives';
import { PeriodTabs } from '../components/PeriodTabs';
import { donemAraligiMetni, SATIS_DONEM_SECENEKLERI } from '../components/satis-analiz/donem';
import { OzetSerit } from '../components/satis-analiz/OzetSerit';
import { ZamanBlok } from '../components/satis-analiz/ZamanBlok';
import { KanalBlok } from '../components/satis-analiz/KanalBlok';
import { SubeBlok } from '../components/satis-analiz/SubeBlok';
import { KategoriBlok } from '../components/satis-analiz/KategoriBlok';
import { UyarilarBlok } from '../components/satis-analiz/UyarilarBlok';
import { CokSatanBlok } from '../components/satis-analiz/CokSatanBlok';
import { TiklaAc } from '../components/satis-analiz/TiklaAc';
import { IsiHaritasiBlok } from '../components/satis-analiz/IsiHaritasiBlok';
import { SepetBlok } from '../components/satis-analiz/SepetBlok';
import { StokSatisBlok } from '../components/satis-analiz/StokSatisBlok';
import { KarKoprusuBlok } from '../components/satis-analiz/KarKoprusuBlok';
import { useSubeler } from '../components/satis-analiz/useSubeler';
import { useSatisAnalizFiltre } from '../components/satis-analiz/useSatisAnalizFiltre';
import { useSatisKaynaklari } from '../components/satis-analiz/ekranKaynaklari';
import { KanalSecici } from '../components/satis-analiz/KanalSecici';
import type { PanelScreen, ReportConfig } from '../types/panelConfig';

const { RangePicker } = DatePicker;

interface SatisAnalizProps {
  screen: PanelScreen;
  reports: ReportConfig[];
}

/**
 * Satis Analiz — S256 tek ekran (G2). Yerlesim SABIT; `config.bloklar`'a bagli DEGIL (SQL 139 ona
 * dokunmaz). Filtre durumu URL'de. Ilk acilis 7 istek: 990040 x2 + 990051-990055 (spec §4.4); tikla-ac
 * bloklari acilana kadar sorgu atmaz.
 *
 * Hucre siniflari: `min-w-0` (recharts ebeveynden genislik okur; grid varsayilani grafigi tasirir),
 * `empty:hidden` (null donen blok yer kaplamasin), `items-start` (kisa kart komsusuna gerilmesin).
 * 🔴 Genislik/ilk ekran hedefi (1440x900) ve telefon 2x2 Bolum D'de tarayicida olculur.
 */
export function SatisAnaliz({ screen, reports }: SatisAnalizProps) {
  const filtre = useSatisAnalizFiltre();
  const { donem, oncekiDonem } = filtre;
  const kaynaklar = useSatisKaynaklari(screen, reports);
  const { subeler, yuklendi } = useSubeler(kaynaklar.kanal040, donem);

  // URL'deki sube listede yoksa (o pencerede satis yok / kapanmis) TUM subelere donulur; liste
  // gelmeden sube gecersiz sayilmaz.
  const sube =
    filtre.sube === null ? null : !yuklendi || subeler.includes(filtre.sube) ? filtre.sube : null;
  const kanal = filtre.kanal;

  const ozelDeger = useMemo<[Dayjs, Dayjs] | null>(
    () => (filtre.ozelAralik ? [dayjs(filtre.ozelAralik.bas), dayjs(filtre.ozelAralik.bit)] : null),
    [filtre.ozelAralik],
  );

  const baslik = screen.config?.baslik?.trim() || screen.title;

  return (
    <div className="flex flex-col gap-4">
      {/* Kaydirinca yapisik filtre bari (spec §3). `bg-page` SART: `tailwind.config.js`'de `surface2`
          tanimli DEGIL (derlenmis CSS'te `bg-surface2` kurali sifir — bar SEFFAF kalirdi ve altindaki
          kartlar yapisik seritin ALTINDAN gecerken gorunurdu). `main` zemini de `bg-page` (Layout.tsx);
          barin zemini sayfayla AYNI renk olunca "yapisik" seffaf degil opak gorunur. `py-2` PageHeader'in
          `mb-5`i sticky div'in DISINDA kaldigi icin (mb kendi disina kayar) govdeye dusey nefes payi verir
          — yoksa yapisik haldeyken govde barin alt kenarina bitisik durur. */}
      <div data-testid="filtre-bari" className="sticky top-0 z-10 bg-page py-2">
        <PageHeader
          title={baslik}
          actions={
            // 🔴 390px'te olculdu (2026-08-21): serit sarmazsa `main` onu KESER, seciciler erisilemez.
            <div className="flex flex-wrap items-center gap-2" data-testid="donem-secici">
              <PeriodTabs
                value={filtre.donemTuru}
                onChange={(p) => filtre.degistir({ donemTuru: p })}
                secenekler={SATIS_DONEM_SECENEKLERI}
              />
              {/* Sube suzgeci: yalniz BIRDEN FAZLA sube varsa anlamli. Tek subeli firmada secenek
                  kutusu gurultudur, hic basilmaz. */}
              {subeler.length > 1 && (
                <div data-testid="sube-secici">
                  <Select
                    size="small"
                    style={{ minWidth: 140 }}
                    value={sube ?? '__tum__'}
                    onChange={(v) => filtre.degistir({ sube: v === '__tum__' ? null : v })}
                    options={[
                      { value: '__tum__', label: 'Tüm şubeler' },
                      ...subeler.map((s) => ({ value: s, label: s })),
                    ]}
                  />
                </div>
              )}
              <KanalSecici value={kanal} onChange={(k) => filtre.degistir({ kanal: k })} />
              {/* 🔴 min-h-[28px] (2026-08-29 olculdu): sube secici sorgu donunce belirir; aralik onun
                  olculmus yuksekligini KOSULSUZ ayirir, yoksa dar ekranda +11px kayma. */}
              <span
                className="inline-flex items-center min-h-[28px] text-[12px] text-ink-500 tnum"
                data-testid="donem-araligi"
              >
                {donemAraligiMetni(donem)}
              </span>
              {filtre.donemTuru === 'ozel' && (
                // RangePicker testid'yi iki input'a yayar; testid sarmalayicida.
                <div data-testid="ozel-aralik">
                  <RangePicker
                    value={ozelDeger}
                    onChange={(v) =>
                      filtre.degistir({
                        ozelAralik:
                          v && v[0] && v[1]
                            ? { bas: v[0].format('YYYY-MM-DD'), bit: v[1].format('YYYY-MM-DD') }
                            : null,
                      })
                    }
                    format="DD.MM.YYYY"
                    allowClear
                  />
                </div>
              )}
            </div>
          }
        />
      </div>

      {/* Satir 1 — dort kart (telefonda 2x2) */}
      <OzetSerit kaynaklar={kaynaklar} donem={donem} oncekiDonem={oncekiDonem} sube={sube} kanal={kanal} />

      {/* Satir 2 — trend 5/12 · kanal 4/12 · sube 3/12.
          `items-start`: bir hucre komsusundan kisaysa GERILMEZ (grid varsayilani `stretch`, kisa karti
          bosluga esnetirdi). `min-w-0` SART: recharts'in ResponsiveContainer'i ebeveyninden genislik
          okur; grid hucresinin varsayilan `min-width:auto` degeri icerigin asgari genisligine kilitlenir
          ve grafik hucreyi TASIRARDI. `empty:hidden`: blok bileseni kendi bos-durum kuralindan `null`
          donerse sarmalayici BOS kalir; gizlenmezse izgarada gorunmez ama YER KAPLAYAN bir hucre birakir. */}
      <div data-testid="satir-2" className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
        <div className="min-w-0 empty:hidden lg:col-span-5">
          <ZamanBlok kaynaklar={kaynaklar} donem={donem} sube={sube} kanal={kanal} />
        </div>
        <div className="min-w-0 empty:hidden lg:col-span-4">
          <KanalBlok reports={reports} donem={donem} sube={sube} kanal={kanal} />
        </div>
        <div className="min-w-0 empty:hidden lg:col-span-3">
          <SubeBlok reports={reports} donem={donem} sube={sube} kanal={kanal} />
        </div>
      </div>

      {/* Satir 3 — kategori · uyarilar · en cok satan 5 */}
      <div data-testid="satir-3" className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
        <div className="min-w-0 empty:hidden lg:col-span-6">
          <KategoriBlok kaynak={kaynaklar.kategori} donem={donem} oncekiDonem={oncekiDonem} sube={sube} kanal={kanal} />
        </div>
        <div className="min-w-0 empty:hidden lg:col-span-3">
          <UyarilarBlok kaynaklar={kaynaklar} donem={donem} oncekiDonem={oncekiDonem} sube={sube} kanal={kanal} />
        </div>
        <div className="min-w-0 empty:hidden lg:col-span-3">
          <CokSatanBlok kaynak={kaynaklar.enCokSatan} donem={donem} sube={sube} kanal={kanal} />
        </div>
      </div>

      {/* Tikla-ac — kapali baslar, acilana kadar sorgu atmaz. Kaynak yoksa (menuden/ekrandan)
          TiklaAc kabugu bile cizilmez — kapali bir kutunun ardinda hicbir zaman acilmayacak bos bir
          blok birakmamak icin. */}
      <div data-testid="tikla-ac-bloklar" className="flex flex-col gap-3">
        {kaynaklar.isi042 && (
          <TiklaAc anahtar="isi" baslik="Saat × gün yoğunluğu" altBaslik="Hangi gün, hangi saatte satış yoğun">
            <IsiHaritasiBlok reports={reports} donem={donem} sube={sube} kanal={kanal} />
          </TiklaAc>
        )}
        {kaynaklar.sepet019 && (
          <TiklaAc anahtar="sepet" baslik="Birlikte satılanlar" altBaslik="Son 90 gün · kanal süzgeci uygulanmaz">
            <SepetBlok reports={reports} sube={sube} kanal={kanal} />
          </TiklaAc>
        )}
        {kaynaklar.cokSatan014 && kaynaklar.kritik025 && (
          <TiklaAc anahtar="stok" baslik="Stok × satış" altBaslik="Satıyor ama stok bitiyor · kanal süzgeci uygulanmaz">
            <StokSatisBlok reports={reports} donem={donem} sube={sube} kanal={kanal} />
          </TiklaAc>
        )}
        {kaynaklar.urunKarliligi && (
          <TiklaAc anahtar="kopru" baslik="Kâr köprüsü" altBaslik="Ciro → maliyet → brüt kâr">
            <KarKoprusuBlok kaynaklar={kaynaklar} donem={donem} sube={sube} kanal={kanal} />
          </TiklaAc>
        )}
      </div>
    </div>
  );
}
